import React, { useState, useEffect } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { Card, Button, Label, Input, Textarea, StatusDot, PageHeader } from "../components/Primitives";
import { updateAvailability } from "../lib/db";

const OPTIONS = [
  { key: "available", bg: "bg-status-available/10", border: "border-status-available/40", text: "text-status-available" },
  { key: "limited", bg: "bg-status-limited/10", border: "border-status-limited/40", text: "text-status-limited" },
  { key: "unavailable", bg: "bg-status-unavailable/10", border: "border-status-unavailable/40", text: "text-status-unavailable" },
];

export default function Availability() {
  const { t } = useI18n();
  const { user, profile, refreshProfile } = useAuth();
  const [status, setStatus] = useState("available");
  const [note, setNote] = useState("");
  const [until, setUntil] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile?.availability) setStatus(profile.availability);
  }, [profile?.availability]);

  const save = async () => {
    if (!user?.id) { toast.error("Please sign in first"); return; }
    setSaving(true);
    try {
      await updateAvailability(user.id, status);
      await refreshProfile();
      toast.success(t("availability.saved"));
    } catch (e) {
      toast.error(e?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto animate-fade-up">
      <PageHeader title={t("availability.title")} subtitle={t("availability.subtitle")} testId="availability-header" />

      <div className="space-y-3 mb-6">
        {OPTIONS.map((o) => (
          <button key={o.key} onClick={() => setStatus(o.key)} data-testid={`availability-${o.key}`}
            className={`w-full text-left p-5 rounded-xl border transition-all ${
              status === o.key ? `${o.bg} ${o.border}` : "bg-carbon-800 border-carbon-600 hover:border-carbon-500"
            }`}
          >
            <div className="flex items-center gap-4">
              <StatusDot status={o.key} />
              <div className="flex-1">
                <div className={`text-base font-semibold ${status === o.key ? o.text : ""}`}>
                  {t(`availability.${o.key}`)}
                </div>
                <div className="text-xs text-ink-500 mt-0.5">{t(`availability.${o.key}Desc`)}</div>
              </div>
              {status === o.key && <Check className={`w-5 h-5 ${o.text}`} />}
            </div>
          </button>
        ))}
      </div>

      {status !== "available" && (
        <Card className="p-5 mb-6 animate-fade-up">
          {status === "unavailable" && (
            <div className="mb-4">
              <Label>{t("availability.until")}</Label>
              <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="availability-until" />
            </div>
          )}
          <Label>{t("availability.note")}</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="..." data-testid="availability-note" />
          <p className="text-[10px] font-mono text-ink-500 mt-2">Note & date are kept locally until the profile-edit page ships.</p>
        </Card>
      )}

      <Button onClick={save} disabled={saving} data-testid="availability-save" className="w-full sm:w-auto">
        {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> …</> : <><Check className="w-4 h-4" /> {t("availability.save")}</>}
      </Button>
    </div>
  );
}
